import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Clock, Info, Infinity, Loader2 } from 'lucide-react'
import { useResource, useUpdateResource } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'

export default function ResourceLimit() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading } = useTelegram()
  const { data: resource, isLoading } = useResource(resourceId)
  const updateResource = useUpdateResource()

  const [limitMode, setLimitMode] = useState<'none' | 'daily' | 'retention'>('none')
  const [dailyLimit, setDailyLimit] = useState<number>(100)
  const [retention, setRetention] = useState<number>(500)
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string>('')

  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize from resource data
  useEffect(() => {
    if (resource && !isInitialized) {
      if (resource.retention && resource.retention > 0) {
        setLimitMode('retention')
        setRetention(resource.retention)
      } else if (resource.resource_limit && resource.resource_limit > 0) {
        setLimitMode('daily')
        setDailyLimit(resource.resource_limit)
      } else {
        setLimitMode('none')
      }
      setIsInitialized(true)
    }
  }, [resource, isInitialized])

  const handleSave = async () => {
    if (!resource) return

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      let updateData: any = {}

      if (limitMode === 'none') {
        updateData = {
          resource_limit: 0,
          retention: 0,
        }
      } else if (limitMode === 'daily') {
        if (dailyLimit < 1 || dailyLimit > 10000) {
          setError('Дневной лимит должен быть от 1 до 10000')
          hapticNotification('error')
          setMainButtonLoading(false)
          return
        }
        updateData = {
          resource_limit: dailyLimit,
          retention: 0,
        }
      } else if (limitMode === 'retention') {
        if (retention < 1 || retention > 100000) {
          setError('Удержание должно быть от 1 до 100000')
          hapticNotification('error')
          setMainButtonLoading(false)
          return
        }
        updateData = {
          resource_limit: 0,
          retention: retention,
        }
      }

      await updateResource.mutateAsync({
        resourceId: resource.id,
        data: updateData,
      })
      hapticNotification('success')

      setMainButtonLoading(false)
      hideMainButton()
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`, {
        replace: true,
        state: { successMessage: 'Ресурс успешно обновлен' }
      })
    } catch (error) {
      console.error('Failed to update limit:', error)
      setError('Ошибка при сохранении лимита')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  handleSaveRef.current = handleSave

  // Auto-manage back button
  useBackButton()

  useEffect(() => {
    if (!isReady) return

    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    setMainButtonLoading(false)
    showMainButton('Сохранить лимит', onSave, '#5288c1', '#ffffff')

    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, setMainButtonLoading])

  if (isLoading || !resource) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Настройка лимита</h1>
              <p className="text-sm text-tg-hint mt-1">{resource.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
        {/* Limit Mode Selection */}
        <div className="bg-tg-secondary-bg rounded-xl p-4 space-y-2">
          <h2 className="font-semibold text-tg-text text-sm">Режим лимита</h2>

          {[
            { value: 'none', label: 'Без лимита', desc: 'Без ограничений' },
            { value: 'daily', label: 'Дневной лимит', desc: 'Ограничение новых подписчиков в день' },
            { value: 'retention', label: 'Удержание', desc: 'Поддержание точного числа подписчиков' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => {
                hapticFeedback('light')
                setLimitMode(option.value as any)
                setError('')
              }}
              className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                limitMode === option.value
                  ? 'border-tg-button bg-tg-button/10'
                  : 'border-tg-bg bg-tg-bg hover:border-tg-hint/30'
              }`}
            >
              <div className="font-medium text-tg-text text-sm">{option.label}</div>
              <div className="text-xs text-tg-hint mt-0.5">{option.desc}</div>
            </button>
          ))}
        </div>

        {/* Daily Limit Settings */}
        {limitMode === 'daily' && (
          <div
            className="rounded-xl p-4 space-y-3 border-2 transition-all"
            style={{
              borderColor: '#ec489933',
              backgroundColor: '#ec48990d'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: '#ec48991a' }}
                >
                  <Clock className="w-5 h-5 text-[#ec4899]" />
                </div>
                <div>
                  <div className="font-semibold text-tg-text">Дневной лимит</div>
                  <div className="text-xs text-tg-hint">Макс. в день</div>
                </div>
              </div>
              <div className="text-xl font-bold text-[#ec4899]">
                {dailyLimit}
              </div>
            </div>

            {/* Slider */}
            <div className="space-y-2">
              <input
                type="range"
                min={10}
                max={1000}
                step={10}
                value={dailyLimit}
                onChange={(e) => {
                  setDailyLimit(parseInt(e.target.value))
                  setError('')
                }}
                onTouchStart={() => hapticFeedback('light')}
                className="w-full h-2 bg-tg-hint/20 rounded-lg appearance-none cursor-pointer slider-thumb"
                style={{
                  background: `linear-gradient(to right, #ec4899 0%, #ec4899 ${((dailyLimit - 10) / (1000 - 10)) * 100}%, rgba(168, 179, 207, 0.2) ${((dailyLimit - 10) / (1000 - 10)) * 100}%, rgba(168, 179, 207, 0.2) 100%)`,
                  ['--slider-color' as any]: '#ec4899'
                }}
              />
              <div className="flex justify-between text-xs text-tg-hint">
                <span>10</span>
                <span>1000</span>
              </div>
            </div>

            {/* Preset Buttons */}
            <div className="flex gap-1.5">
              {[50, 100, 250, 500, 1000].map((val) => (
                <button
                  key={val}
                  onClick={() => {
                    hapticFeedback('light')
                    setDailyLimit(val)
                    setError('')
                  }}
                  className="flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all"
                  style={
                    dailyLimit === val
                      ? { backgroundColor: '#ec4899', color: '#ffffff' }
                      : { backgroundColor: '#ec48991a', color: '#ec4899' }
                  }
                >
                  {val}
                </button>
              ))}
            </div>

            {/* Number Input */}
            <div className="relative">
              <input
                type="number"
                min={1}
                max={10000}
                value={dailyLimit}
                onChange={(e) => {
                  const value = parseInt(e.target.value)
                  if (!isNaN(value)) {
                    setDailyLimit(value)
                    setError('')
                  }
                }}
                onFocus={() => hapticFeedback('light')}
                className="w-full px-4 py-2.5 text-sm font-medium text-center border-2 rounded-lg focus:outline-none transition-colors bg-tg-bg text-tg-text"
                style={{
                  borderColor: '#ec489933'
                }}
                placeholder="Введите лимит"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-tg-hint">
                /день
              </div>
            </div>
          </div>
        )}

        {/* Retention Settings */}
        {limitMode === 'retention' && (
          <div
            className="rounded-xl p-4 space-y-3 border-2 transition-all"
            style={{
              borderColor: '#05966933',
              backgroundColor: '#0596690d'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: '#0596691a' }}
                >
                  <Infinity className="w-5 h-5 text-[#059669]" />
                </div>
                <div>
                  <div className="font-semibold text-tg-text">Удержание</div>
                  <div className="text-xs text-tg-hint">Активных подписчиков</div>
                </div>
              </div>
              <div className="text-xl font-bold text-[#059669]">
                {retention}
              </div>
            </div>

            {/* Slider */}
            <div className="space-y-2">
              <input
                type="range"
                min={100}
                max={10000}
                step={100}
                value={retention}
                onChange={(e) => {
                  setRetention(parseInt(e.target.value))
                  setError('')
                }}
                onTouchStart={() => hapticFeedback('light')}
                className="w-full h-2 bg-tg-hint/20 rounded-lg appearance-none cursor-pointer slider-thumb"
                style={{
                  background: `linear-gradient(to right, #059669 0%, #059669 ${((retention - 100) / (10000 - 100)) * 100}%, rgba(168, 179, 207, 0.2) ${((retention - 100) / (10000 - 100)) * 100}%, rgba(168, 179, 207, 0.2) 100%)`,
                  ['--slider-color' as any]: '#059669'
                }}
              />
              <div className="flex justify-between text-xs text-tg-hint">
                <span>100</span>
                <span>10000</span>
              </div>
            </div>

            {/* Preset Buttons */}
            <div className="flex gap-1.5">
              {[500, 1000, 2500, 5000, 10000].map((val) => (
                <button
                  key={val}
                  onClick={() => {
                    hapticFeedback('light')
                    setRetention(val)
                    setError('')
                  }}
                  className="flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all"
                  style={
                    retention === val
                      ? { backgroundColor: '#059669', color: '#ffffff' }
                      : { backgroundColor: '#0596691a', color: '#059669' }
                  }
                >
                  {val}
                </button>
              ))}
            </div>

            {/* Number Input */}
            <div className="relative">
              <input
                type="number"
                min={1}
                max={100000}
                value={retention}
                onChange={(e) => {
                  const value = parseInt(e.target.value)
                  if (!isNaN(value)) {
                    setRetention(value)
                    setError('')
                  }
                }}
                onFocus={() => hapticFeedback('light')}
                className="w-full px-4 py-2.5 text-sm font-medium text-center border-2 rounded-lg focus:outline-none transition-colors bg-tg-bg text-tg-text"
                style={{
                  borderColor: '#05966933'
                }}
                placeholder="Введите количество"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-tg-hint">
                активных
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-600/10 border-2 border-red-600/20 rounded-xl">
            <p className="text-sm text-red-600 text-center font-medium">{error}</p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-tg-secondary-bg rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-tg-button flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-tg-text mb-1.5">Подробнее о режимах</h3>
              <p className="text-sm text-tg-hint leading-relaxed">
                Здесь будет подробное описание.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
